// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import styles from './PoolExportDataInvoice.module.scss';
import { useTranslation } from 'react-i18next';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { useEffect, useMemo, useState } from 'react';
import CheckboxField from 'components/atoms/CheckboxField';
import { FilterInvoiceData, PoolInvoiceData } from 'util/Types';
import { normalizeNumber } from 'util/commons';
import Csv from 'util/csv';
import dayjs from 'dayjs';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { getSlaFormatYearMonthDate } from 'ts/formatDate';
import DOMPurify from 'dompurify';
import { getAdminPoolInvoices } from 'apis/invoice';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { getStations } from 'apis/commons';
import { Form } from 'react-final-form';

const PoolExportDataInvoice = () => {
  const { t } = useTranslation();
  const [showItem, setShowItem] = useState(false);

  const initialFilter = useMemo(() => {
    return {
      dateFrom: dayjs().startOf('month').format('YYYY/M/DD'),
      dateTo: dayjs().format('YYYY/M/DD'),
    };
  }, []);

  const [invoiceFilter, setInvoiceFilter] =
    useState<Partial<FilterInvoiceData>>(initialFilter);
  const [stationOptions, setStationOptions] = useState<SelectOption[]>([]);
  const [invoiceData, setInvoiceData] = useState<PoolInvoiceData>({
    ...initialFilter,
    invoices: [],
  });

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    if (invoiceFilter.stationId) {
      fetchInvoiceData();
    }
  }, [invoiceFilter]);

  const invoiceExportMonth = useMemo(() => {
    return t('pool_export_data_invoice.invoice_export_month', {
      year: dayjs(invoiceFilter.dateTo).format('YYYY'),
      month: dayjs(invoiceFilter.dateTo).format('MM'),
    });
  }, [t, invoiceFilter]);

  const fetchStations = () => {
    getStations({
      successCallback: (data) => {
        const sortedData = data.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        setStationOptions(
          sortedData.map((item) => ({
            value: item.id,
            label: item.name,
          }))
        );

        if (!invoiceFilter.stationId) {
          setInvoiceFilter((prev) => ({
            ...prev,
            stationId: sortedData[0]?.id,
          }));
        }
      },
    });
  };

  const fetchInvoiceData = () => {
    if (invoiceFilter.dateFrom && invoiceFilter.dateTo) {
      const params: FilterInvoiceData = {
        dateFrom: invoiceFilter.dateFrom,
        dateTo: invoiceFilter.dateTo,
        stationId: invoiceFilter.stationId,
      };

      getAdminPoolInvoices({
        params,
        successCallback: (res) => {
          setInvoiceData(res);
        },
        failedCallback: () => {
          setInvoiceData({
            ...params,
            invoices: [],
          });
        },
      });
    }
  };

  const handleChangeDateRange = (value: [string, string] | undefined) => {
    setInvoiceFilter((prev) => ({
      ...prev,
      dateFrom: value?.[0] ? getSlaFormatYearMonthDate(value[0]) : undefined,
      dateTo: value?.[1] ? getSlaFormatYearMonthDate(value[1]) : undefined,
    }));
  };

  const csvVerShowItemHeader: (string | [string, string])[] = useMemo(() => {
    return [
      ['invoiceMonth', t('pool_export_data_invoice.invoice_month')],
      ['siteName', t('pool_export_data_invoice.site_name')],
      ['itemName', t('pool_export_data_invoice.item')],
      ['price', t('pool_export_data_invoice.price')],
      ['weight', t('pool_export_data_invoice.weight')],
      ['cost', t('pool_export_data_invoice.invoice_amount')],
    ];
  }, [t]);

  const csvHeader: (string | [string, string])[] = useMemo(() => {
    return [
      ['invoiceMonth', t('pool_export_data_invoice.invoice_month')],
      ['siteName', t('pool_export_data_invoice.site_name')],
      ['totalCost', t('pool_export_data_invoice.invoice_amount')],
    ];
  }, [t]);

  const getInvoiceDataCsv = (invoices: PoolInvoiceData['invoices']) => {
    if (showItem) {
      return {
        headers: csvVerShowItemHeader,
        data: invoices.map((item) => {
          if (item?.items?.length) {
            return item?.items?.map((it) => ({
              invoiceMonth: invoiceExportMonth,
              siteName: item.siteName,
              itemName: it.itemName,
              price: normalizeNumber({
                value: it.price,
                toFixed: 0,
              }),
              weight: normalizeNumber({ value: it.weight }),
              cost: normalizeNumber({
                value: it.cost,
                toFixed: 0,
              }),
            }));
          }

          return {
            invoiceMonth: invoiceExportMonth,
            siteName: item.siteName,
            itemName: '',
            price: 0,
            weight: 0,
            cost: 0,
          };
        }),
      };
    }

    return {
      headers: csvHeader,
      data: invoices.map((item) => ({
        invoiceMonth: invoiceExportMonth,
        siteName: item.siteName,
        totalCost: normalizeNumber({
          value: item.totalCost,
          toFixed: 0,
        }),
      })),
    };
  };

  const handleDownloadCSV = () => {
    const invoices = getInvoiceDataCsv(invoiceData.invoices);
    const stationName = stationOptions.find(
      ({ value }) => value === invoiceFilter.stationId
    )?.label;

    const fileNameCsv = `${stationName}_${t(
      'pool_export_data_invoice.site_invoice'
    )}_${dayjs(invoiceData.dateTo).format('YYYYMM')}`;

    new Csv({
      headers: invoices.headers,
      data: invoiceData.invoices.length ? invoices.data.flat() : [],
      headNote: `${dayjs(invoiceData.dateFrom).format('YYYY/MM/DD')}-${dayjs(
        invoiceData.dateTo
      ).format('YYYY/MM/DD')}`,
    }).downLoadCsv({
      filename: fileNameCsv,
    });
  };

  return (
    <PrimaryTemplate
      h1={t('pool_export_data_invoice.heading')}
      className={styles.exportDataInvoice}
    >
      <div className={styles.exportDataInvoiceManage}>
        <div
          className={styles.countList}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              t('common.page', {
                page: invoiceData.invoices.length,
              }) ?? ''
            ),
          }}
        />

        <form className={styles.formInput}>
          <div className={styles.inputFrame}>
            <div className={styles.inputFrameWrap}>
              <Form onSubmit={() => {}}>
                {() => (
                  <div className={styles.filterStation}>
                    <SelectField
                      name="stationId"
                      value={String(invoiceFilter.stationId)}
                      placeholder={t('pool_dashboard.filter.stations')}
                      options={stationOptions}
                      onChange={(value) =>
                        setInvoiceFilter((prev) => ({
                          ...prev,
                          stationId: Number(value),
                        }))
                      }
                      isDarkmode
                      showPlaceholderOption
                      searchable
                    />
                  </div>
                )}
              </Form>
              <div className={styles.filterDateRange}>
                <TableFilterCalendar
                  expanded={{
                    placeholder: t('common.placeholder.select_date_range'),
                    icon: <IconCalendarTablet />,
                  }}
                  fullWidth
                  value={[
                    invoiceFilter.dateFrom || '',
                    invoiceFilter.dateTo || '',
                  ]}
                  allowClear={false}
                  onChange={handleChangeDateRange}
                  chosenDatesLimit={{
                    limit: 60,
                    required: true,
                  }}
                />
              </div>
            </div>

            <div className={styles.inputFrameWrap}>
              <div className={styles.showItem}>
                <CheckboxField
                  title={t('pool_export_data_invoice.show_item')}
                  input={{
                    checked: showItem,
                    onChange: (e) => setShowItem(e.target.checked),
                  }}
                />
              </div>

              <button type="button" onClick={handleDownloadCSV}>
                <IconDownload />
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className={styles.exportDataInvoiceTable}>
        <table>
          <thead>
            <tr>
              <th>{t('pool_export_data_invoice.invoice_month')}</th>
              <th
                className={showItem ? styles.siteName : styles.siteNameColSpan2}
              >
                {t('pool_export_data_invoice.site_name')}
              </th>
              {showItem && (
                <>
                  <th>{t('pool_export_data_invoice.item')}</th>
                  <th>{t('pool_export_data_invoice.price')}</th>
                  <th>{t('pool_export_data_invoice.weight')}</th>
                </>
              )}
              <th className={styles.textAlignRight}>
                {t('pool_export_data_invoice.invoice_amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.invoices.length > 0 ? (
              invoiceData.invoices.map((invoice) => {
                return (
                  <tr key={invoice.siteId}>
                    <td>
                      <p>{invoiceExportMonth}</p>
                    </td>
                    <td>
                      <p>{invoice.siteName}</p>
                    </td>
                    {showItem ? (
                      invoice.items.length ? (
                        <td colSpan={4} className={styles.itemAndTotal}>
                          {invoice.items?.map((item, index) => {
                            return (
                              <tr
                                key={index}
                                className={styles.itemAndTotalItem}
                              >
                                <td>
                                  <p>{item.itemName}</p>
                                </td>
                                <td className={styles.textAlignRight}>
                                  <p>
                                    {normalizeNumber({
                                      value: item.price,
                                      toFixed: 0,
                                    })}
                                  </p>
                                </td>
                                <td className={styles.textAlignRight}>
                                  <p>
                                    {normalizeNumber({ value: item.weight })}
                                  </p>
                                </td>
                                <td className={styles.textAlignRight}>
                                  <p>
                                    {normalizeNumber({
                                      value: item.cost,
                                      toFixed: 0,
                                    })}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}
                        </td>
                      ) : (
                        <>
                          <td></td>
                          <td className={styles.textAlignRight}>
                            <p>0</p>
                          </td>
                          <td className={styles.textAlignRight}>
                            <p>0</p>
                          </td>
                          <td className={styles.textAlignRight}>
                            <p>0</p>
                          </td>
                        </>
                      )
                    ) : (
                      <td className={styles.textAlignRight}>
                        <p>
                          {normalizeNumber({
                            value: invoice.totalCost,
                            toFixed: 0,
                          })}
                        </p>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className={styles.msgError}>
                  {t('messages.M_008')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PrimaryTemplate>
  );
};

export default PoolExportDataInvoice;
